import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { LegalPage, P, UL } from "@/components/marketing/LegalPage";

export const Route = createFileRoute("/legal/affiliate-global")({
  head: () => ({
    meta: [
      { title: "Banana Partners Affiliate Program (Global) — banana.cards" },
      { name: "description", content: "Terms & Conditions of the Banana Partners Affiliate Program for global partners promoting Banana Cards online." },
      { property: "og:title", content: "Banana Partners Affiliate Program (Global) — banana.cards" },
      { property: "og:description", content: "Commission structure, tracking, payout, and brand rules for global Banana Cards affiliate partners." },
    ],
    links: alternateLinkList("/legal/affiliate-global", "en"),
  }),
  component: AffiliateGlobalPage,
});

export function AffiliateGlobalPage() {
  return (
    <LegalPage
      eyebrow="Legal · Partners"
      title="Banana Partners"
      italic="Affiliate Program."
      updated="January 2026"
      intro="Turn your network into recurring income. Share Banana Cards online, earn commissions on every hardware sale and software subscription — no stock, no upfront cost, no logistics. Everything is tracked automatically and paid fast via your affiliate dashboard."
      related={[
        { label: "Affiliate Program — Vietnam", to: "/legal/affiliate-vietnam" },
        { label: "Retailer Agreement — Global", to: "/legal/retailer-global" },
        { label: "Terms of Service", to: "/terms" },
      ]}
      sections={[
        {
          id: "overview",
          title: "Program Highlights",
          body: (
            <>
              <P><strong>No inventory. No risk.</strong> Affiliates do not hold stock, purchase, store or ship products — you simply promote using your affiliate link or QR code.</P>
              <P><strong>Simple commissions:</strong> 25% on hardware sales and 25% recurring on software subscriptions, calculated exclusive of VAT and tracked automatically.</P>
              <P><strong>Recurring income (no expiry):</strong> Subscription commissions are paid in perpetuity for as long as the referred customer remains subscribed. No caps, no 12-month limits, no resets.</P>
              <P><strong>Built-in discount code:</strong> Each affiliate receives a 10% discount code that can be shared with customers or used personally. Commissions still apply on discounted purchases.</P>
              <P><strong>Automatic tracking & attribution</strong> via affiliate links, QR codes, and system-verified attribution. No spreadsheets, no manual claims, no retroactive commission requests.</P>
              <P><strong>Fast, transparent payouts:</strong> Commissions are visible in real time inside the partner dashboard and withdrawable on demand, paid within 3–5 business days after request.</P>
              <P><strong>Banana Cards handles everything:</strong> production, encoding, packaging, fulfilment, subscription billing, and customer support.</P>
              <P><strong>Clean exit. No lock-in.</strong> Either party may terminate at any time; previously earned commissions remain payable.</P>
            </>
          ),
        },
        {
          id: "purpose",
          title: "1. Purpose",
          body: (
            <>
              <P>This Agreement authorises the Affiliate to promote Banana Cards products and services online through approved referral methods and to earn commissions on qualifying transactions.</P>
              <P>Affiliates operate strictly as referral partners and are not retailers, resellers, distributors, or consignment partners.</P>
            </>
          ),
        },
        {
          id: "relationship",
          title: "2. Affiliate Relationship",
          body: (
            <>
              <P>The Affiliate:</P>
              <UL items={[
                "Acts as an independent contractor",
                "Has no authority to bind Banana Cards",
                "Is not an employee, franchisee, agent, or representative",
              ]} />
              <P>No exclusivity or territory rights are granted.</P>
            </>
          ),
        },
        {
          id: "program",
          title: "3. Program Overview",
          body: (
            <>
              <P>Affiliates are provided with:</P>
              <UL items={[
                "A unique affiliate tracking link and/or QR code",
                "A 10% discount code which may be shared with customers or used personally for online purchases",
                "Access to the Banana Cards partner dashboard",
              ]} />
              <P>All tracking and attribution are managed automatically by the platform.</P>
            </>
          ),
        },
        {
          id: "commission",
          title: "4. Commission Structure",
          body: (
            <>
              <P><strong>Hardware sales:</strong> 25% commission on Banana Cards hardware purchased online, calculated exclusive of VAT.</P>
              <P><strong>Software subscriptions:</strong> 25% recurring commission on subscription revenue, paid for as long as the referred customer remains subscribed.</P>
              <P>Affiliates do not earn retail margins and do not hold or resell stock.</P>
            </>
          ),
        },
        {
          id: "commission-changes",
          title: "4a. Changes to Commission Rates and Commercial Terms",
          body: (
            <>
              <P>Banana Cards reserves the right to update, amend, increase, decrease, replace, or otherwise modify the commission rates, discount structures, payout rules, eligibility rules, or other commercial terms of the Affiliate Program from time to time.</P>
              <P>Any such changes shall apply only to new customers, new referrals, new transactions, new subscription activations, or new qualifying sales generated after the effective date of the updated terms.</P>
              <P>Customers who were validly referred by the Affiliate before the effective date of any change shall remain governed by the commission rates and commercial terms that applied at the time of their original attribution, provided that:</P>
              <UL items={[
                "valid attribution continues to exist;",
                "the customer remains active and paying;",
                "the Affiliate remains compliant with this Agreement; and",
                "no fraud, misuse, prohibited conduct, or breach of this Agreement has occurred.",
              ]} />
              <P>For the avoidance of doubt, existing referred customers shall not be automatically moved to reduced commission rates solely because Banana Cards changes its Affiliate Program terms for future customers.</P>
              <P>Banana Cards shall provide written notice of any material change to commission rates or commercial terms. The notice shall specify the effective date of the new terms. Continued participation in the Affiliate Program after the effective date shall constitute acceptance of the updated terms for future referrals and future qualifying transactions only.</P>
            </>
          ),
        },
        {
          id: "duration",
          title: "5. Commission Duration",
          body: (
            <>
              <P>All eligible commissions are paid in perpetuity, provided that:</P>
              <UL items={[
                "valid attribution exists",
                "the customer remains active",
                "the Affiliate remains compliant with this Agreement",
              ]} />
            </>
          ),
        },
        {
          id: "tracking",
          title: "6. Tracking & Attribution",
          body: (
            <>
              <P>Commission eligibility relies exclusively on affiliate links, affiliate QR codes, and system-verified attribution records.</P>
              <P>Manual claims, screenshots, verbal confirmation, or retroactive attribution are not accepted. If attribution cannot be verified by the system, no commission is payable.</P>
            </>
          ),
        },
        {
          id: "discount",
          title: "7. Discount Code Usage",
          body: (
            <>
              <P>Each Affiliate is issued a unique 10% discount code that may be shared publicly or privately with customers, or used by the Affiliate for personal purchases.</P>
              <P>Discount codes do not reduce the Affiliate's commission base, cannot be combined with other promotions unless approved, and may be modified or revoked at Banana Cards' discretion.</P>
            </>
          ),
        },
        {
          id: "calc",
          title: "8. Commission Calculation Rules",
          body: (
            <UL items={[
              "All commissions are calculated exclusive of VAT",
              "Refunds, chargebacks, failed payments, and free trials do not generate commission",
              "Subscription commissions stop automatically when billing stops",
            ]} />
          ),
        },
        {
          id: "payment",
          title: "9. Commission Payment Terms",
          body: (
            <>
              <P>Commissions are tracked automatically, visible in real time, and withdrawable via the partner dashboard.</P>
              <P>Upon valid withdrawal request, payments are released within 3–5 business days and may be made in VND or other supported currencies.</P>
            </>
          ),
        },
        {
          id: "tax",
          title: "9a. Tax Treatment",
          body: (
            <>
              <P><strong>Individual Affiliates (Vietnamese residents):</strong> Personal Income Tax (PIT) is withheld at the statutory rate (currently 10%); Banana Cards remits tax on the Affiliate's behalf; the net amount is paid after withholding.</P>
              <P><strong>Company Affiliates (Vietnam legal entities):</strong> Must issue a valid tax invoice; VAT (if applicable) is added on top of commission; payment is processed after invoice verification.</P>
              <P>Affiliates are solely responsible for tax compliance in their jurisdiction.</P>
            </>
          ),
        },
        {
          id: "responsibilities",
          title: "10. Affiliate Responsibilities",
          body: (
            <UL items={[
              "Promote Banana Cards honestly and accurately",
              "Use only approved brand assets and messaging",
              "Disclose affiliate relationships where legally required",
              "Comply with advertising, consumer protection, and platform policies",
            ]} />
          ),
        },
        {
          id: "prohibited",
          title: "11. Prohibited Conduct",
          body: (
            <>
              <P>The Affiliate must not:</P>
              <UL items={[
                "Misrepresent product features or pricing",
                "Offer unauthorised discounts",
                "Use paid ads bidding on Banana Cards trademarks without approval",
                "Create misleading or deceptive marketing",
                "Promote competing NFC card products or direct clones",
                "Manipulate tracking or attribution systems",
              ]} />
              <P>Violation may result in immediate termination.</P>
            </>
          ),
        },
        {
          id: "marketplace",
          title: "11a. Marketplace Sales Restriction",
          body: (
            <>
              <P>The Affiliate expressly agrees that they are not authorised to list, advertise, or sell Banana Cards products on any public or third-party online marketplaces, including but not limited to:</P>
              <UL items={[
                "Shopee, Lazada, Amazon, TikTok Shop, eBay, Alibaba / AliExpress",
                "Facebook Marketplace",
                "Any similar e-commerce or social commerce platform",
              ]} />
              <P>This restriction applies to physical Banana Cards hardware, digital products or subscriptions, bundles, promotions, or derivative listings — and to listings under the Affiliate's own name, company name, or any third party acting on their behalf.</P>
              <P>Affiliates may promote Banana Cards only through official affiliate links, affiliate QR codes, and approved marketing campaigns directing users to Banana Cards' official website or checkout.</P>
              <P><strong>Enforcement:</strong> Any unauthorised marketplace listing constitutes a material breach and may result in immediate suspension of affiliate tracking links and discount codes, forfeiture of unpaid commissions, termination without notice, and takedown requests submitted directly to the marketplace.</P>
            </>
          ),
        },
        {
          id: "branding",
          title: "12. Branding & Marketing Use",
          body: (
            <>
              <P>Affiliates may use approved logos, product images, marketing copy, and campaign materials supplied by Banana Cards.</P>
              <P>Affiliates may not modify branding, create derivative product names, or represent themselves as official distributors or retailers.</P>
            </>
          ),
        },
        {
          id: "confidentiality",
          title: "13. Confidentiality",
          body: (
            <P>All non-public information — including commission structures, dashboards, pricing frameworks, and attribution logic — is confidential and may not be disclosed to third parties.</P>
          ),
        },
        {
          id: "term",
          title: "14. Term & Termination",
          body: (
            <>
              <P>This Agreement remains effective until terminated. Either Party may terminate at any time with written notice.</P>
              <P>Immediate termination applies if fraud or manipulation is detected, brand misrepresentation occurs, or prohibited competitive activity is identified.</P>
              <P><strong>Effect of Termination:</strong> Previously earned commissions remain payable; no new commissions accrue after the termination date; affiliate links and codes may be deactivated.</P>
            </>
          ),
        },
        {
          id: "liability",
          title: "15. Limitation of Liability",
          body: (
            <>
              <P>Banana Cards shall not be liable for indirect or consequential damages, loss of expected income, or attribution failures outside system control.</P>
              <P>Total liability is limited to unpaid commissions properly earned.</P>
            </>
          ),
        },
        {
          id: "law",
          title: "16. Governing Law",
          body: (
            <P>This Agreement shall be governed by and interpreted under the laws of Vietnam.</P>
          ),
        },
        {
          id: "entire",
          title: "17. Entire Agreement",
          body: (
            <>
              <P>This Agreement constitutes the entire understanding between the Parties and supersedes any prior discussions or representations relating to the Affiliate Program.</P>
              <P>Banana Cards may update the Affiliate Program terms in accordance with Section 4a. Any updated terms shall apply only from their stated effective date and shall not affect commission rights already attached to existing validly attributed customers, unless otherwise agreed in writing or where the Affiliate has breached this Agreement.</P>
            </>
          ),
        },
      ]}
    />
  );
}